import { useCallback, useEffect, useState } from "react";
import { useRealtimeKitMeeting } from "@cloudflare/realtimekit-react";

export function getLivestreamDeviceLabel({
  device,
  fallback,
}: {
  device: MediaDeviceInfo;
  fallback: string;
}) {
  const label = device.label?.trim();
  return label || fallback;
}

export function useLivestreamMediaDevices() {
  const { meeting } = useRealtimeKitMeeting();
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [speakerDevices, setSpeakerDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedAudioDeviceId, setSelectedAudioDeviceId] = useState("");
  const [selectedVideoDeviceId, setSelectedVideoDeviceId] = useState("");
  const [selectedSpeakerDeviceId, setSelectedSpeakerDeviceId] = useState("");
  const [ready, setReady] = useState(false);

  const refreshDevices = useCallback(async () => {
    if (!meeting) return;
    const [audio, video, speakers] = await Promise.all([
      meeting.self.getAudioDevices(),
      meeting.self.getVideoDevices(),
      meeting.self.getSpeakerDevices(),
    ]);
    const current = meeting.self.getCurrentDevices();
    setAudioDevices(audio);
    setVideoDevices(video);
    setSpeakerDevices(speakers);
    setSelectedAudioDeviceId(current.audio?.deviceId ?? "");
    setSelectedVideoDeviceId(current.video?.deviceId ?? "");
    setSelectedSpeakerDeviceId(current.speaker?.deviceId ?? "");
    setReady(true);
  }, [meeting]);

  useEffect(() => {
    if (!meeting) return;
    setReady(false);
    void refreshDevices();

    const sync = () => {
      void refreshDevices();
    };

    meeting.self.on("deviceListUpdate", sync);
    meeting.self.on("deviceUpdate", sync);

    return () => {
      meeting.self.off("deviceListUpdate", sync);
      meeting.self.off("deviceUpdate", sync);
    };
  }, [meeting, refreshDevices]);

  const selectAudioDevice = useCallback(
    async ({ deviceId }: { deviceId: string }) => {
      if (!meeting || !deviceId || deviceId === selectedAudioDeviceId) return;
      const device = await meeting.self.getDeviceById(deviceId, "audio");
      await meeting.self.setDevice(device);
      setSelectedAudioDeviceId(deviceId);
    },
    [meeting, selectedAudioDeviceId],
  );

  const selectVideoDevice = useCallback(
    async ({ deviceId }: { deviceId: string }) => {
      if (!meeting || !deviceId || deviceId === selectedVideoDeviceId) return;
      const device = await meeting.self.getDeviceById(deviceId, "video");
      await meeting.self.setDevice(device);
      setSelectedVideoDeviceId(deviceId);
    },
    [meeting, selectedVideoDeviceId],
  );

  const selectSpeakerDevice = useCallback(
    async ({ deviceId }: { deviceId: string }) => {
      if (!meeting || !deviceId || deviceId === selectedSpeakerDeviceId) return;
      const device = await meeting.self.getDeviceById(deviceId, "speaker");
      await meeting.self.setDevice(device);
      setSelectedSpeakerDeviceId(deviceId);
    },
    [meeting, selectedSpeakerDeviceId],
  );

  return {
    ready,
    audioDevices,
    videoDevices,
    speakerDevices,
    selectedAudioDeviceId,
    selectedVideoDeviceId,
    selectedSpeakerDeviceId,
    selectAudioDevice,
    selectVideoDevice,
    selectSpeakerDevice,
  };
}
